// @ts-nocheck

import * as R from 'ramda';
import { MONTH_GRANULARITY } from '../../../../../../shared/constants/analytics';
import getCurrencySymbol from '../../../../../../shared/utils/getCurrencySymbol';
import { POSTBACK_SEQUENCE_INDEX_LABEL } from '../../../../main/screens/ScreenSKAdNetwork/ScreenSKAdNetworkConstants';
import {
  TIMESERIES_DATE_FORMAT,
  TIMESERIES_MONTH_FORMAT,
  convertToTimezone,
  createMomentObjInTimezone,
} from '../../../../main/support/dateUtils';
import { commas, roundToFixed2 } from '../../../../main/support/utils';
import type { GroupByResponse, GroupByResult, Query } from '../../../../main/types/druidTypes';
import type { AxesParameters } from '../../../../main/types/timeseriesTypes';
import { getColumnMetadata, injectTimeseriesLocalCurrency } from '../../../support/utils';
import { DRUID_TO_HUMAN_READABLE } from '../../eo_mapping';
import {
  CORE_DATASOURCES,
  COST,
  COST_ACTIONS,
  COST_CLICKS,
  COST_IMPRESSIONS,
  COST_INSTALLS,
  ECPA,
  ECPA_IN_LOCAL_CURRENCY,
  ECPC,
  ECPC_IN_LOCAL_CURRENCY,
  ECPI,
  ECPI_IN_LOCAL_CURRENCY,
  ECPM,
  ECPM_IN_LOCAL_CURRENCY,
  EO_COMMERCE_EVENT,
  EO_INSTALL,
  SK_ADNETWORK_DATASOURCE,
  ZEIT_CLICK,
  ZEIT_IMPRESSION,
} from '../../eo_mapping/datasources';
import {
  CALCULATED_COST_EVENT_NAMES,
  CALCULATED_EVENT_NAMES,
  CALCULATED_REVENUE_EVENT_NAMES,
  LOCAL_CURRENCY_EVENTS_HUMAN_READABLE,
  LOCAL_CURRENCY_EVENTS_TO_HUMAN_READABLE,
  REVENUE_IN_LOCAL_CURRENCY_HUMAN_READABLE,
  REVENUE_MEASURE_EVENT_NAMES,
} from '../../eo_mapping/event_names';
import { CUSTOM_EVENT_PREFIX, UNDISCLOSED_EVENT_VALUE } from '../eo_constants';
import { getColumnDisplay } from '../eo_helpers';
import { convertSKANPostbackIndexToHumanReadable } from './convertSKANPostbackIndexToHumanReadable';
import { getLabelFromEventDescription, parseEventDescriptionFromEventKeyOrUndefined } from './events';
import {
  TIMESERIES_LABEL_AMOUNT,
  TIMESERIES_LABEL_AMOUNT_IN_LOCAL_CURRENCY,
  TIMESERIES_LABEL_COUNT,
  TIMESERIES_LABEL_RETURN_PERCENTAGE,
  TIMESERIES_METRIC_COST,
  TIMESERIES_METRIC_COST_IN_APP_LOCAL_CURRENCY,
  TIMESERIES_METRIC_PERCENTAGE,
  TIMESERIES_METRIC_REVENUE,
  TIMESERIES_METRIC_REVENUE_IN_LOCAL_CURRENCY,
  TIMESERIES_METRIC_TOTAL_COUNT,
  TIMESERIES_METRIC_UNIQUE_COUNT,
} from './timeseriesConstants';

export type ProcessDruidTimeseriesDataParameters = {
  data: GroupByResponse;
  dimensions: Array<string>;
  events: Array<string>;
  unique: boolean;
};
const FALLBACK_DIMENSION_VALUE = 'Unpopulated';
const ATTRIBUTED_DIMENSION_VALUE = 'attributed';
const DRUID_ORGANIC_RESPONSE_VALUE = 'false (organic)';
const ORGANIC_DISPLAY_VALUE = 'Organic';

/**
 * Tick values need to be formatted differently
 *
 * @param {Array} dates - list of dates
 * @return {Array} reformatted dates
 */
export function getTickValuesFromDates(dates: string[]): Record<string, any>[] {
  if (!dates || !dates.length) {
    return [];
  }

  return dates.map((date) => {
    // provide correct dateRange format to createMomentObjInTimezone by replacing '/' with '-'
    const formattedDate = date.replace(/\//gi, '-');
    return createMomentObjInTimezone(formattedDate);
  });
}

/*
 * method that extracts the first date from a date range
 *
 * leaves day and month dates as they are
 *   "2019/12/27" => "2019/12/27"
 *   "2019/12" => "2019/12"
 *
 * extracts the first date from a week range
 *   "2019/12/22 - 2019/12/28" => "2019/12/22"
 *
 * @param {string} dateRange (part of the event object for each record from Cenarius)
 * @result {string} dateString
 *
 */
export const getFirstDateInRange = (dateRange: string) => dateRange.split(' - ')[0];

/*
 * method that returns dimension values based on selected dimensions and the groupByResult
 *
 * special cases:
 *   does not return a label for the 'attributed' dimension for an attributed event
 *   returns 'Organic' instead of 'false (organic)' (what comes back from Druid) for the 'attributed' dimension for an organic event
 */
export const getDimensionValues = (dimensions: string[], groupByResult: GroupByResult) => {
  const dimensionValues = dimensions.map((dim) => {
    let value = groupByResult.event[dim] || groupByResult.event[DRUID_TO_HUMAN_READABLE[dim]] || FALLBACK_DIMENSION_VALUE;
    const isEventOrganic =
      dimensions.includes(ATTRIBUTED_DIMENSION_VALUE) && groupByResult.event.attributed === DRUID_ORGANIC_RESPONSE_VALUE;

    // do not show a label for the 'attributed' dimension for an attributed event
    if (dim === ATTRIBUTED_DIMENSION_VALUE && value === 'true') {
      value = null;
    }

    if (dim === POSTBACK_SEQUENCE_INDEX_LABEL) {
      value = convertSKANPostbackIndexToHumanReadable(value);
    }

    // show 'Organic' instead of 'false (organic)' for the 'attributed' dimension for an organic event
    if (isEventOrganic && dim === ATTRIBUTED_DIMENSION_VALUE) {
      value = ORGANIC_DISPLAY_VALUE;
    }

    // do not show 'Unpopulated' for an organic event
    if (isEventOrganic && dim !== ATTRIBUTED_DIMENSION_VALUE && value === FALLBACK_DIMENSION_VALUE) {
      value = null;
    }

    return value;
  });
  return dimensionValues.filter(Boolean);
};

const getHumanReadableDruidEventName = (eventName: string): string => {
  if (CORE_DATASOURCES[eventName]) {
    return CORE_DATASOURCES[eventName];
  }

  if (LOCAL_CURRENCY_EVENTS_TO_HUMAN_READABLE[eventName]) {
    return LOCAL_CURRENCY_EVENTS_TO_HUMAN_READABLE[eventName];
  }

  return eventName;
};

/**
 * Method that creates a comma separated string of the following values:
 * <Event>, <...Dimension Values>, <timestamp>
 *
 * @param {GroupByResult} groupByResult
 * @param {string} event
 * @param {Array<string>} dimensions
 * @param {boolean} withTimestamp
 * @return {string}
 */
export const generateCommaSeparated = (
  groupByResult: GroupByResult,
  event: string,
  dimensions: Array<string>,
  withTimestamp?: boolean
) => {
  const humanReadableEventName = getHumanReadableDruidEventName(event);
  let dateRange = (groupByResult.event && groupByResult.event.date_range) || '';
  // use only the first date in the date range as the key (applicable for week granularity)
  dateRange = getFirstDateInRange(dateRange);
  const timestampPart = withTimestamp ? ', ' + dateRange : '';

  if (!dimensions.length) {
    return `${humanReadableEventName}${timestampPart}`;
  }

  const dimensionValues = getDimensionValues(dimensions, groupByResult);
  return dimensionValues.length
    ? `${humanReadableEventName}, ${dimensionValues.join(', ')}${timestampPart}`
    : `${humanReadableEventName}${timestampPart}`;
};

/**
 * Method that generates an Object where the key is a comma separated representation of a GroupByResult, and
 * the value is an Array of GroupByResults that correspond to the key.
 * This data structure can be used to do a constant time lookup of all GroupByResults with common tuple values of the following tuple: (event, dimension values, timestamp)
 *
 * Example Response
 * {
      "install, IOS, IOS_APP, 2019-01-01": [
        {
          timestamp: "2019-01-01",
          event: {
            total_count: 5,
            user_data_os: "IOS",
            user_data_platform: "IOS_APP"
          }
        }
      ]
 * }
 *
 * @param {GroupByResponse} data
 * @param {string[]} dimensions
 * @param {string} event
 * @param {boolean | undefined} withTimestamp
 * @return see example response above
 */
export const generateMapOfSeriesToRecords = (
  data: GroupByResponse,
  dimensions: string[],
  event: string,
  withTimestamp?: boolean
) => {
  return Object.keys(data).reduce((acc, datasource: any) => {
    const groupByResult = data[datasource];

    if (!groupByResult || !Array.isArray(groupByResult)) {
      return acc;
    }

    groupByResult.forEach((record) => {
      if (!record.event || !Object.keys(record.event).length) {
        return;
      }

      // don't proceed if date_range is the only key in the record's event object
      // this is a result of Cenarius logic which adds date_range even for zero-filled records
      // TODO: rip this out when EG-2988 is complete since Cenarius will no longer be sending records as described above (they will be empty again as desired)
      const eventKeys = Object.keys(record.event);

      if (eventKeys.length === 1 && eventKeys[0] === 'date_range') {
        return;
      }

      if (
        !(
          // case 1: the event matches the datasource
          // this case should catch both core datasources (eo_install) and calculated datasource (eCPI)
          (
            event === datasource || // case 2: the event matches the metric
            // this case should catch revenue
            Object.keys(record.event).includes(event) || // case 3: the event matches the name value
            // this case should catch named events like VIEW_ITEM
            event === record.event.name || // case 4: the event is a custom event
            event === (record.event.name && `${CUSTOM_EVENT_PREFIX}${record.event.name}`)
          )
        )
      ) {
        return;
      }

      let displayedEvent = event;

      if (event.includes(CUSTOM_EVENT_PREFIX)) {
        displayedEvent = getColumnDisplay(event);
      }

      if (event === SK_ADNETWORK_DATASOURCE) {
        displayedEvent = DRUID_TO_HUMAN_READABLE[event];
      }

      const commaSeparatedKey = generateCommaSeparated(record, displayedEvent, dimensions, withTimestamp);
      acc[commaSeparatedKey] = acc[commaSeparatedKey] ? [...acc[commaSeparatedKey], record] : [record];
    });

    return acc;
  }, {});
};

/**
 * Get the metric that corresponds to the (event, unique) tuple
 *
 * @param {string} event
 * @param {boolean} unique
 * @return {string} metric
 */
export const parseMetric = (event: string, unique: boolean = false) => {
  const metric = unique ? TIMESERIES_METRIC_UNIQUE_COUNT : TIMESERIES_METRIC_TOTAL_COUNT;

  if (REVENUE_MEASURE_EVENT_NAMES.includes(event)) {
    return TIMESERIES_METRIC_REVENUE;
  }

  if (REVENUE_IN_LOCAL_CURRENCY_HUMAN_READABLE.includes(event)) {
    return TIMESERIES_METRIC_REVENUE_IN_LOCAL_CURRENCY;
  }

  if (event === COST || CALCULATED_COST_EVENT_NAMES[event]) {
    return TIMESERIES_METRIC_COST;
  }

  if (LOCAL_CURRENCY_EVENTS_HUMAN_READABLE.includes(event)) {
    return TIMESERIES_METRIC_COST_IN_APP_LOCAL_CURRENCY;
  }

  if (CALCULATED_REVENUE_EVENT_NAMES[event]) {
    return TIMESERIES_METRIC_PERCENTAGE;
  }

  return metric;
};

/**
 * Generates a zero filled GroupByRecord with the given dimension values and metric set to 0
 *
 * Example Response:
 * {
    timestamp: "2019/01/01",
    event: {
      total_count: 0,
      user_data_os: "IOS",
      user_data_platform: "IOS_APP"
    }
 * }
 *
 * @param {string} key - comma separated string of (event, dimension values...)
 * @param {string} dimensions
 * @param {string} date_range
 * @param {boolean} unique
 * @return zero filled GroupByRecord
 */
export const getZeroFilledGroupByRecordFromKey = (
  key: string,
  dimensions: Array<string>,
  date_range: string,
  unique: boolean
) => {
  const eventAndDimensionValues = key.split(', ');
  const event = eventAndDimensionValues[0];
  const metric = parseMetric(event, unique);
  const dimensionValues = eventAndDimensionValues.slice(1);
  // don't include date_range dimension since key doesn't include it
  const dimensionsFinal = dimensions.filter((dim) => dim !== 'date_range');
  const initialEventMap = {
    [metric]: 0,
  };
  let eventMap = dimensionsFinal.reduce((acc, dimension, index) => {
    return R.assoc(dimension, dimensionValues[index], acc);
  }, initialEventMap);

  // add date_range value to eventMap if necessary
  if (dimensions.includes('date_range')) {
    eventMap = R.assoc('date_range', date_range, eventMap);
  }

  return {
    timestamp: date_range,
    event: eventMap,
  };
};

/**
 * constructs an object where the keys are titles of lineseries and values are an array of GroupByRecords ordered chronologically by timestamp
 *
 * Example Result:
 * {
    "install, IOS, IOS_APP": [
      {
        timestamp: "2019-01-01",
        event: {
          total_count: 1,
          user_data_os: "IOS",
          user_data_platform: "IOS_APP"
        }
      },
      {
        timestamp: "2019-01-02",
        event: {
          total_count: 2,
          user_data_os: "IOS",
          user_data_platform: "IOS_APP"
        }
      },
    ]
 * }
 *
 * @param {Array<string>} lineSeriesKeys - array of lineseries names
 * @param {Array<string>} dates - array of timestamps in the format 'YYYY-MM-DD'
 * @param {Object} recordMap - object generated by a call to generateMapOfSeriesToRecords
 * @param {Array<string>} dimensions
 * @param {boolean} unique
 * @result - see example result
 */
export const hydrateLineSeries = (
  lineSeriesKeys: Array<string>,
  dates: Array<string>,
  recordMap: Record<string, any>,
  dimensions: Array<string>,
  unique: boolean
) => {
  return lineSeriesKeys.reduce((allSeries, seriesKey) => {
    dates.forEach((date) => {
      const key = `${seriesKey}, ${date}`;
      const eventAndDimensionValues = seriesKey.split(', ');
      const event = eventAndDimensionValues[0];
      const metric = parseMetric(event, unique);
      const record = recordMap[key]
        ? recordMap[key].reduce(
            (acc, record) => {
              const eventMetricUndisclosed = record.event[metric] === UNDISCLOSED_EVENT_VALUE;
              const metricWithUndisclosedData = acc.event[metric] || UNDISCLOSED_EVENT_VALUE;
              const event = {
                ...acc.event,
                ...record.event,
                [metric]: eventMetricUndisclosed
                  ? metricWithUndisclosedData
                  : acc.event[metric] + parseFloat(record.event[metric]),
              };
              return { ...acc, ...record, event };
            },
            {
              event: {
                [metric]: 0,
              },
            }
          )
        : getZeroFilledGroupByRecordFromKey(seriesKey, dimensions, date, unique);
      let seriesName = seriesKey;
      const eventDescription = parseEventDescriptionFromEventKeyOrUndefined(event);

      if (eventDescription) {
        seriesName =
          [getLabelFromEventDescription(eventDescription), ...eventAndDimensionValues.slice(1)].join(', ') || seriesName;
      }

      allSeries[seriesName] = allSeries[seriesName] ? [...allSeries[seriesName], record] : [record];
    });
    return allSeries;
  }, {});
};

/**
 * given a string of comma separated values where the first value is an event name, this method returns that event name
 *
 * @param {string} commaSeparated
 * @return {string} event
 */
export const parseEvent = (commaSeparated: string) => {
  return commaSeparated.split(', ')[0];
};

/**
 * converts a GroupByResult into a React-vis point
 *
 * @param {GroupByResult} groupByRecord
 * @param {string} lineSeriesName
 * @param {boolean} unique
 * @result react vis point, which is an object with an x parameter and a y parameter
 */
export const convertGroupByRecordToPoint = (groupByRecord: GroupByResult, lineSeriesName: string, unique: boolean) => {
  const event = parseEvent(lineSeriesName);
  const metric = parseMetric(event, unique);
  const groupByEvent = groupByRecord.event;
  const dateRange = (groupByEvent && groupByEvent.date_range) || '';
  // provide correct dateRange format to createMomentObjInTimezone by replacing '/' with '-'
  const formattedDateRange = dateRange.replace(/\//gi, '-');
  const yPointValue =
    groupByEvent[metric] === UNDISCLOSED_EVENT_VALUE ? UNDISCLOSED_EVENT_VALUE : parseFloat(groupByEvent[metric] || 0);
  return {
    x: createMomentObjInTimezone(formattedDateRange),
    y: yPointValue,
  };
};

/*
 * method that takes an object generated from a call to hydrateLineSeries and converts its values (which are an array of GroupByResults) into an array of react vis points
 *
 * @param {Object} hydratedLineSeries
 * @param {Array<Moment>} tickValues - an array of moment values
 * @param {boolean} unique
 */
export const toTimeseriesFormat = (hydratedLineSeries: Record<string, any>, unique: boolean) => {
  return Object.keys(hydratedLineSeries).reduce((acc, lineSeriesName) => {
    const series = hydratedLineSeries[lineSeriesName].map((record) =>
      convertGroupByRecordToPoint(record, lineSeriesName, unique)
    );
    const data = {
      data: series,
    };
    return R.assoc(lineSeriesName, data, acc);
  }, {});
};

export const getDates = (data: GroupByResponse) => {
  if (!data) {
    return [];
  }

  const timeseriesData = Object.values(data)[0];

  if (!timeseriesData || !Array.isArray(timeseriesData)) {
    return [];
  }

  return timeseriesData
    .filter((record) => record.event.date_range !== undefined)
    .map((record) => getFirstDateInRange(record.event.date_range))
    .filter((item, index, arr) => arr.indexOf(item) >= index)
    .sort();
};

export const getTickValues = (data: GroupByResponse) => {
  if (!data) {
    return [];
  }

  const dates = getDates(data);
  return getTickValuesFromDates(dates);
};

/**
 * Entry point for taking a GroupByResponse and converting it into a react vis lineseries object
 *
 * @param {GroupByResponse} data
 * @param {dimensions} dimensions
 * @param {Array<string>} events
 * @param {boolean} unique
 * @result {Object} react vis lineseries object
 */
export const processDruidTimeseriesData = (
  data: GroupByResponse,
  dimensions: Array<string>,
  events: Array<string>,
  unique: boolean
) => {
  if (!data) {
    return [];
  }

  const dates = getDates(data);
  // don't include date range in lineSeriesKeys
  const filteredDimensions = dimensions.filter((dim) => dim !== 'date_range');
  return events.map((event) => {
    const mapOfSeriesToRecords = generateMapOfSeriesToRecords(data, filteredDimensions, event, true);
    const lineSeriesKeys = Object.keys(generateMapOfSeriesToRecords(data, filteredDimensions, event, false));
    const hydratedLineSeries = hydrateLineSeries(lineSeriesKeys, dates, mapOfSeriesToRecords, dimensions, unique);
    return toTimeseriesFormat(hydratedLineSeries, unique);
  });
};

/**
 * Helper function for takeTopNResults
 */
function lastIndexYValue(arr) {
  const length = arr.length;
  return arr[length - 1].y;
}

/**
 * Given a set of timeseries data, return top n results. Defaults to 6
 * Used when splitting timeseries by dimension that has more than n values
 * to prevent from showing too many results.
 *
 * @params {Object} timeseries chart data split by a dimension
 * @params {Number} optional - number of results to return
 * @returns {Object} subset of original timeseries chart data
 */
export const takeTopNResults = (data: Record<string, any>, n?: number | null | undefined): Record<string, any> => {
  n = n || 6;

  if (R.keys(data).length <= n) {
    return data;
  }

  // order by latest data points y value
  const order = [];

  for (const key in data) {
    if (!data.hasOwnProperty(key)) {
      continue;
    }

    const item = data[key].data;

    if (item) {
      const y = Math.round(lastIndexYValue(item));

      if (!order[y]) {
        order[y] = {};
      }

      order[y][key] = key;
    }
  }

  // traverse from greatest to least, taking only n results
  const results = {};
  let count = 0;

  for (let i = order.length - 1; i >= 0; i--) {
    const index = order[i];

    if (!index) {
      continue;
    }

    const keys = Object.keys(index);

    for (let j = 0; j < keys.length; j++) {
      const key = keys[j];
      results[key] = data[key];
      count++;

      if (count === n) {
        return results;
      }
    }
  }

  return results;
};

const getCalculatedDatasources = () => {
  const COST_DATASOURCES = {
    [ECPI]: {
      fn: '/',
      fields: [
        {
          data_source: COST_INSTALLS,
        },
        {
          data_source: EO_INSTALL,
        },
      ],
    },
    [ECPI_IN_LOCAL_CURRENCY]: {
      calculation: {
        fn: '/',
        fields: [
          {
            data_source: COST_INSTALLS,
            metric: 'cost_in_app_local_currency',
          },
          {
            data_source: EO_INSTALL,
          },
        ],
      },
    },
    [ECPC]: {
      fn: '/',
      fields: [
        {
          data_source: COST_CLICKS,
        },
        {
          data_source: ZEIT_CLICK,
        },
      ],
    },
    [ECPC_IN_LOCAL_CURRENCY]: {
      calculation: {
        fn: '/',
        fields: [
          {
            data_source: COST_CLICKS,
            metric: 'cost_in_app_local_currency',
          },
          {
            data_source: ZEIT_CLICK,
          },
        ],
      },
    },
    [ECPM]: {
      fn: '/',
      fields: [
        {
          data_source: COST_IMPRESSIONS,
        },
        {
          fn: '/',
          fields: [
            {
              data_source: ZEIT_IMPRESSION,
            },
            {
              constant: 1000,
            },
          ],
        },
      ],
    },
    [ECPM_IN_LOCAL_CURRENCY]: {
      calculation: {
        fn: '/',
        fields: [
          {
            data_source: COST_IMPRESSIONS,
            metric: 'cost_in_app_local_currency',
          },
          {
            fn: '/',
            fields: [
              {
                data_source: ZEIT_IMPRESSION,
              },
              {
                constant: 1000,
              },
            ],
          },
        ],
      },
    },
    [ECPA]: {
      fn: '/',
      fields: [
        {
          data_source: COST_ACTIONS,
        },
        {
          data_source: EO_COMMERCE_EVENT,
          name: 'PURCHASE',
        },
      ],
    },
    [ECPA_IN_LOCAL_CURRENCY]: {
      calculation: {
        fn: '/',
        fields: [
          {
            data_source: COST_ACTIONS,
            metric: 'cost_in_app_local_currency',
          },
          {
            data_source: EO_COMMERCE_EVENT,
            name: 'PURCHASE',
          },
        ],
      },
    },
  };
  return {
    ...COST_DATASOURCES,
    GROSS_PROFIT: {
      fn: '-',
      fields: [
        {
          data_source: EO_COMMERCE_EVENT,
          name: 'PURCHASE',
          metric: 'revenue',
        },
        {
          data_source: COST,
        },
      ],
    },
    ROI: {
      fn: '/',
      metric: 'percentage',
      fields: [
        {
          fn: '-',
          fields: [
            {
              data_source: EO_COMMERCE_EVENT,
              name: 'PURCHASE',
              metric: 'revenue',
            },
            {
              data_source: COST,
            },
          ],
        },
        {
          data_source: COST,
        },
      ],
    },
    ROAS: {
      fn: '/',
      metric: 'percentage',
      fields: [
        {
          data_source: EO_COMMERCE_EVENT,
          name: 'PURCHASE',
          metric: 'revenue',
        },
        {
          data_source: COST,
        },
      ],
    },
  };
};

// calculated datasource calculations
const CALCULATED_DATASOURCES = getCalculatedDatasources();

/**
 * prepends 'calculated_' to datasource and updates query
 *
 * @param {Query} query
 */
export const parseCalculatedDatasources = (query: Query): Query => {
  const { data_sources } = query;
  const parsedDatasources = data_sources?.map((ds) => {
    if (!Object.keys(CALCULATED_DATASOURCES).includes(ds)) {
      return ds;
    }

    return `calculated_${ds}`;
  });
  return R.assoc('data_sources', parsedDatasources, query);
};

/**
 * formats a large value (represented as a number or string) with a 'k' for each thousand
 *
 * @param {number | string} val
 * @return formatted large value
 */
export const formatLargeVal = (val: number | string): string => {
  // biome-ignore lint/suspicious/noDoubleEquals: ignored using `--suppress`
  if (typeof val == 'string') {
    val = +val;
  }

  if (val < 1000) {
    return `${roundToFixed2(val)}`;
  }

  return `${commas(roundToFixed2(val / 1000))}k`;
};
// TODO: will need to update this for cohorts

/**
 * resolves y axis title
 *
 * @param {string} metric
 * @return {string} y axis title
 */
export const getYAxisTitle = (metric: string) => {
  if (metric === TIMESERIES_METRIC_PERCENTAGE) {
    return TIMESERIES_LABEL_RETURN_PERCENTAGE;
  }

  if (metric.includes('local_currency')) {
    return TIMESERIES_LABEL_AMOUNT_IN_LOCAL_CURRENCY;
  }

  return [TIMESERIES_METRIC_REVENUE, TIMESERIES_METRIC_COST].indexOf(metric) > -1
    ? TIMESERIES_LABEL_AMOUNT
    : TIMESERIES_LABEL_COUNT;
};

/**
 * partitions lineseries by the y axis title that each line corresponds to
 *
 * Example lineSeries:
 * {
    "install, IOS": {
      data: [
        { x: <MOMENT>, y: 1},
        {x: <MOMENT>, y: 2}
      ]
    },
    {
    "revenue, IOS": {
      data: [
        { x: <MOMENT>, y: 1 },
        { x: <MOMENT>, y: 2 }
      ]
    },
 * }
 *
 * Example Result:
 * {
    count: { revenue: data: <same as lineseries data above>},
    revenue: { revenue: data: <same as lineseries data above>}
 * }
 *
 * @param {Object} lineSeries
 * @param {AxesParameters} axesParameters
 */
export const partitionLineSeriesByAxes = (lineSeries: Record<string, any>, axesParameters: AxesParameters) => {
  return Object.keys(lineSeries).reduce((acc, lineSeriesName) => {
    const event = parseEvent(lineSeriesName);
    const metric = parseMetric(event);
    const title = getYAxisTitle(metric, axesParameters?.localCurrency);
    const lineSeriesByAxis = acc[title]
      ? R.assoc(lineSeriesName, lineSeries[lineSeriesName], acc[title])
      : {
          [lineSeriesName]: lineSeries[lineSeriesName],
        };
    return R.assoc(title, lineSeriesByAxis, acc);
  }, {});
};

/**
 * format value to be displayed on y axis tick
 *
 * @param {number} v - value to be formatted
 * @param {string} columnMetadataType
 * @param {Object.<string, any>} eventMetadata
 * @param {string} timeseriesMetricName
 * @param {boolean} shouldFormatLargeVal
 * @return formatted value of y axis tick
 */
const getYAxisTickFormat = (
  v: number,
  columnMetadataType: string,
  eventMetadata: Record<string, any>,
  timeseriesMetricName: string,
  shouldFormatLargeVal: boolean
) => {
  // format values with 'k' if over 1000
  if (shouldFormatLargeVal) {
    const val = formatLargeVal(v);
    const isEventCurrencyRelated =
      columnMetadataType === 'currency_formula' ||
      [
        TIMESERIES_METRIC_REVENUE,
        TIMESERIES_METRIC_REVENUE_IN_LOCAL_CURRENCY,
        TIMESERIES_METRIC_COST,
        TIMESERIES_METRIC_COST_IN_APP_LOCAL_CURRENCY,
      ].includes(timeseriesMetricName);
    return isEventCurrencyRelated ? `${getCurrencySymbol(eventMetadata.localCurrency || 'USD')} ${val}` : val;
  }

  // format values using eventMetadata formatter. see eo_helpers.js.
  return eventMetadata.formatter
    ? eventMetadata.formatter(v, null, {
        localCurrency: eventMetadata.localCurrency || 'USD',
      })
    : roundToFixed2(v);
};

/**
 * get an object that contains all the metadata associated with a y axis
 *
 * @param {string} eventName
 * @param {string} title - title of the y axis
 * @param {string} axesParameters - axis parameters like local currency
 * @return {Object} y axis metadata which includes the y axis title and the callback method used to format y axis ticks
 */
export const getYAxis = (eventName: string, title: string, axesParameters: AxesParameters = {}) => {
  let columnMetadataType = '  ';
  const currencyFormulasEventsNames = [
    ...LOCAL_CURRENCY_EVENTS_HUMAN_READABLE,
    ...Object.keys(CALCULATED_EVENT_NAMES),
    ...REVENUE_MEASURE_EVENT_NAMES,
  ];

  if (currencyFormulasEventsNames.includes(eventName)) {
    columnMetadataType = 'currency_formula';
  }

  if (CALCULATED_REVENUE_EVENT_NAMES[eventName]) {
    columnMetadataType = 'percentage';
  }

  const eventMetadata = getColumnMetadata(columnMetadataType, null, eventName);
  const eventMetadataWithCurrency = injectTimeseriesLocalCurrency(eventMetadata, axesParameters.localCurrency);
  return {
    title,
    tickFormat: (v: number) => {
      return getYAxisTickFormat(v, columnMetadataType, eventMetadataWithCurrency, eventName, true);
    },
    hintFormat: (v: number) => {
      return getYAxisTickFormat(v, columnMetadataType, eventMetadataWithCurrency, eventName, false);
    },
  };
};

/**
 * partitions lineSeries based on y axes
 *
 * @param {Object} lineSeries
 * @param {Array<string>} events
 * @param {{localCurrency?: string}} axesParameters
 * @return object with (1) lineSeries partitioned by y axis and (2) array of y axis objects
 */
export const constructMultiAxesConfig = (
  lineSeries: Record<string, any>,
  // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
  events: Array<string>,
  axesParameters: AxesParameters
) => {
  const partitionedLineSeriesByAxes = partitionLineSeriesByAxes(lineSeries, axesParameters);
  return Object.keys(partitionedLineSeriesByAxes).map((axisTitle) => {
    const data = partitionedLineSeriesByAxes[axisTitle];
    const eventName = parseEvent(Object.keys(data)[0]);
    const yAxis = getYAxis(eventName, axisTitle, axesParameters);
    return {
      data,
      yAxis,
    };
  });
};

/**
 * determines the correct tick format based on the selected granularity
 *
 * @param {string} granularity
 * @return function which will format dateRange (could be a day, week, or month) correctly
 *
 * day format: 'MM/DD'
 * week format: 'MM/DD' (will show up as `Week of MM/DD` where MM/DD is the first date in the range)
 * month format: 'YYYY/MM' (will show up as `Month of YYYY/MM`)
 */
export const determineTickFormat = (granularity: string) => {
  if (granularity === MONTH_GRANULARITY) {
    return (tickValue: string) => convertToTimezone(tickValue, TIMESERIES_MONTH_FORMAT);
  }

  return (tickValue: string) => convertToTimezone(tickValue, TIMESERIES_DATE_FORMAT);
};
